// Third-party
import React from 'react';
import moment from 'moment';

// Componets
import { IconButton } from '../../../components/buttons';
import { ContactPastille, PillLabel } from '../../../components/misc';
import ConnectedPrivateComponent from '../../../components/PrivateComponent';

// Utils
import { FormattedMessage, formatMessage } from '../../../../utils/i18n';
import { BMOi18n } from '../../../../utils/bmo-gif/constants';
import { getAge } from '../../../../utils/time';
import { PERMISSIONS, userHasPermission } from '../../../../utils/permissions';
import GenericContact from '@/types/generic-contact';
import Locale from '@/types/locale';
import User from '@/types/user';

type Props = {
  user: User;
  contact: GenericContact;
  onEdit: () => void;
  onDelete: () => void;
  isGIF: boolean;
  locale: Locale;
};

export default class ContactDetailsInfo extends React.Component<Props> {
  renderDetails = () => {
    const { contact } = this.props;
    const hasContactLocale = userHasPermission(this.props.user, PERMISSIONS.CONTACTS_LOCALE);
    const hasContactLastName = userHasPermission(this.props.user, PERMISSIONS.CONTACTS_LAST_NAME);
    const hasContactType = userHasPermission(this.props.user, PERMISSIONS.CONTACTS_TYPE);
    const hasContactStage = userHasPermission(this.props.user, PERMISSIONS.CONTACTS_STAGE);
    const hasContactEmail = userHasPermission(this.props.user, PERMISSIONS.CONTACTS_EMAIL);
    return (
      <>
        <FormattedMessage id="Contacts.ContactDetailsInfo.BasicInformation" />

        <div className="contact-fields-group full-width">
          <div>
            {hasContactType && contact.contactType != null && (
              <PillLabel className="contact-pill" text={formatMessage(`Contacts.ContactTypes.${contact.contactType}`)} />
            )}
            {hasContactStage && contact.stage != null && <PillLabel className="contact-pill" text={formatMessage(`Contacts.ContactStages.${contact.stage}`)} />}
          </div>
        </div>

        {contact.uid && (
          <div className="contact-fields-group">
            <div className="contact-field">
              <span className="field-label">
                <FormattedMessage id="Contacts.ContactDetailsInfo.ID" />
              </span>

              <span className="ellipsis" data-private data-dd-privacy="mask">
                {contact.uid}
              </span>
            </div>
          </div>
        )}

        <div className="contact-fields-group">
          <div className="contact-field">
            <span className="field-label">
              <FormattedMessage id="Contacts.ContactDetailsInfo.FirstName" />
            </span>

            <span className="ellipsis" data-private data-dd-privacy="mask">
              {contact.fname}
            </span>
          </div>

          {hasContactLastName && (
            <div className="contact-field">
              <span className="field-label">
                <FormattedMessage id="Contacts.ContactDetailsInfo.LastName" />
              </span>

              <span className="ellipsis" data-private data-dd-privacy="mask">
                {contact.lname}
              </span>
            </div>
          )}
        </div>

        {hasContactEmail && (
          <div className="contact-fields-group full-width">
            <div className="contact-field">
              <span className="field-label">
                <FormattedMessage id="Contacts.ContactDetailsInfo.Email" />
              </span>

              <span className="ellipsis" data-private data-dd-privacy="mask">
                {contact.email}
              </span>
            </div>
          </div>
        )}

        {hasContactLocale && contact.locale && (
          <div className="contact-fields-group full-width">
            <div className="contact-field">
              <span className="field-label">
                <FormattedMessage id="Contacts.ContactDetailsInfo.Locale" />
              </span>
              <span data-private data-dd-privacy="mask">
                {formatMessage(`Common.Language.${contact.locale}`)}
              </span>
            </div>
          </div>
        )}

        {contact.note && (
          <div className="contact-fields-group full-width">
            <div className="contact-field">
              <span className="field-label">
                <FormattedMessage id="Contacts.ContactDetailsInfo.Notes" />
              </span>

              <span data-private data-dd-privacy="mask">
                {contact.note}
              </span>
            </div>
          </div>
        )}
      </>
    );
  };

  renderGIFDetails = () => {
    const { contact, locale } = this.props;
    const messages = BMOi18n[locale];

    return (
      <>
        <h3>{messages.ANNUITANT}</h3>

        <div className="contact-fields-group">
          <div className="contact-field">
            <span className="field-label">{messages.FIRST_NAME}</span>

            <span className="ellipsis" data-private data-dd-privacy="mask">
              {contact.fname}
            </span>
          </div>

          <div className="contact-field">
            <span className="field-label">{messages.LAST_NAME}</span>

            <span className="ellipsis" data-private data-dd-privacy="mask">
              {contact.lname}
            </span>
          </div>
        </div>

        <div className="contact-fields-group">
          <div className="contact-field">
            <span className="field-label">{messages.DOB}</span>

            <span data-private data-dd-privacy="mask">
              {moment(contact.dob).format(formatMessage('Common.DateFormats.Short'))}
            </span>
          </div>

          {contact.dob && (
            <div className="contact-field">
              <span className="field-label">{messages.AGE}</span>

              <span data-private data-dd-privacy="mask">
                {getAge(contact.dob)}
              </span>
            </div>
          )}
        </div>

        <div className="contact-fields-group full-width">
          <div className="contact-field">
            <span className="field-label">{messages.POLICYOWNER_ANNUITANT_OPTION}</span>

            <span>{contact.ownerIsAnnuitant ? messages.YES : messages.NO}</span>
          </div>
        </div>

        {contact.fnamePolicyOwner && (
          <>
            <h3>{messages.POLICYOWNER_1}</h3>

            <div className="contact-fields-group">
              <div className="contact-field">
                <span className="field-label">{messages.FIRST_NAME}</span>

                <span className="ellipsis" data-private data-dd-privacy="mask">
                  {contact.fnamePolicyOwner}
                </span>
              </div>

              <div className="contact-field">
                <span className="field-label">{messages.LAST_NAME}</span>

                <span className="ellipsis" data-private data-dd-privacy="mask">
                  {contact.lnamePolicyOwner}
                </span>
              </div>
            </div>
          </>
        )}

        {contact.fnameSecondPolicyOwner && (
          <>
            <h3>{messages.POLICYOWNER_2}</h3>

            <div className="contact-fields-group">
              <div className="contact-field">
                <span className="field-label">{messages.FIRST_NAME}</span>

                <span className="ellipsis" data-private data-dd-privacy="mask">
                  {contact.fnameSecondPolicyOwner}
                </span>
              </div>

              <div className="contact-field">
                <span className="field-label">{messages.LAST_NAME}</span>

                <span className="ellipsis" data-private data-dd-privacy="mask">
                  {contact.lnameSecondPolicyOwner}
                </span>
              </div>
            </div>
          </>
        )}

        <span>{messages.ACCOUNT_DETAILS}</span>

        <div className="contact-fields-group">
          <div className="contact-field">
            <span className="field-label">{messages.ACCOUNT_TYPE}</span>

            <span>
              {contact.accountType}
              {contact.registeredAccountType ? `, ${contact.registeredAccountType}` : ''}
            </span>
          </div>
        </div>

        {contact.province && (
          <div className="contact-fields-group">
            <div className="contact-field">
              <span className="field-label">{messages.PROVINCE}</span>

              <span>{contact.province}</span>
            </div>
          </div>
        )}
      </>
    );
  };

  render() {
    const { contact, isGIF } = this.props;

    const editForcedFalse = contact.availableActions != null && !contact.availableActions.edit;
    const deleteForcedFalse = contact.availableActions != null && !contact.availableActions.delete;

    return (
      <div className="contact-details-info">
        <div className="contact-details-actions">
          <ConnectedPrivateComponent forcedFalse={editForcedFalse} permissions={[PERMISSIONS.CONTACTS_EDIT]}>
            {() => <IconButton className="edit-contact-btn" icon="pencil" onClick={this.props.onEdit} />}
          </ConnectedPrivateComponent>
          <ConnectedPrivateComponent forcedFalse={deleteForcedFalse} permissions={[PERMISSIONS.CONTACTS_DELETE]}>
            {() => <IconButton className="delete-contact-btn" icon="ds-delete" onClick={this.props.onDelete} />}
          </ConnectedPrivateComponent>
        </div>

        <ContactPastille contact={contact} />

        <div className="contact-details-wrapper">{isGIF ? this.renderGIFDetails() : this.renderDetails()}</div>
      </div>
    );
  }
}
